import { jsx } from "panel";
import produce from "immer";
import capitalize from "lodash/capitalize";
import isNumber from "lodash/isNumber";
import { ButtonSize, ButtonTheme, ButtonType } from "common/components/button/constants";
import { QueryBlockEl } from "common/query-builder/query-block";
import * as PropertiesMenu from "common/query-builder/query-block/properties-menu/types";
import { MenuItemType } from "common/query-builder/query-block/properties-menu/types";
import { EntryPart } from "common/query-builder/query-entry-section/types";
import { SelectTheme } from "common/components/select/constants";
import { SelectEl } from "common/components/select";
import { BEHAVIOR_AGGREGATION } from "common/query-builder/query-block/properties-menu/constants";
import {
  propertySelectionToSegmentation,
  segmentationToPropertySelection
} from "common/query-builder/query-block/segment-menu/util";
import { RowFilters } from "common/query-builder/query-entry-section/components/filters";
import { PropertyFilter } from "common/widgets/property-filter-menu/models/property-filter";
import { DisplayContext as PropDisplayContext } from "common/widgets/property-filter-menu/models/types";
import { PropertyFilterFactory } from "common/widgets/property-filter-menu/models/utils";
import { renameEvent } from "common/query-builder/cohorts-query-builder/util";
import { getPropertyName } from "common/query-builder/query-block/count-menu/util";
import { PropertyValuesScreenEl } from "common/widgets/property-filter-menu/property-values-screen";
import { ButtonEl } from "common/components/button";
import { Placement } from "common/components/tooltip/types";
import { MultiAttributionLookbackEl } from "common/widgets/multi-attribution-lookback";
import { SPECIAL_EVENTS } from "common/report/constants";
import {
  DEFAULT_AGGREGATION_BEHAVIOR,
  DEFAULT_BEHAVIORLESS_SEGMENTATION,
  DEFAULT_FREQUENCY_BEHAVIOR,
  DEFAULT_MULTI_ATTRIBUTION_BEHAVIOR,
  generateAggregationsList,
  generateStepList,
  getAggregationLabelForBehavior,
  getColumnLabelForBehavior,
  getDefaultPropertyValueForBehavior,
  isAggregationBehavior,
  isAggregationSegmentation,
  isEventBasedBehavior,
  isEventBasedBehavioralSegmentation,
  isMultiAttributionSegmentation
} from "./utils";
export function BehavioralSubItem({
  selected,
  handleSelect,
  index,
  isPlaceholder,
  section,
  selectorIcon,
  entry,
  displayCohorts = true,
  isForFilter = false
}) {
  var _a, _b;
  const currentSelection = selected;
  const behavior = currentSelection.behavior;
  const { index: activeIndex, part: activePart } = section.state.openEntry;
  const handleAggregationSelection = (ev) => {
    const { value: aggregation } = ev.detail.item;
    const selection = addBehaviorToSegmentation(currentSelection, {
      ...behavior,
      aggregationOperator: aggregation.operator
    });
    handleSelect({ ...currentSelection, ...selection });
  };
  const handleStepSelection = (ev) => {
    const { value: startStepNumber } = ev.detail.item;
    const selection = addBehaviorToSegmentation(currentSelection, {
      ...behavior,
      startStepNumber,
      endStepNumber: startStepNumber + 1
    });
    handleSelect({ ...currentSelection, ...selection });
  };
  const sections = generateAggregationsList(behavior);
  const label = getAggregationLabelForBehavior(behavior);
  const stepSections = generateStepList(behavior, section.attr(`step-count`));
  return /* @__PURE__ */ jsx("div", {
    sel: ".mp-query-sub-selector"
  }, isEventBasedBehavior(behavior) && isNumber(behavior.startStepNumber) && /* @__PURE__ */ jsx("div", {
    sel: ".subitem-text"
  }, /* @__PURE__ */ jsx(SelectEl, {
    sel: ".subitem-select",
    attrs: {
      elref: `stepSelect`,
      "disable-search": true,
      tabindex: -1,
      theme: SelectTheme.QbSubitem,
      size: ButtonSize.Small,
      open: activeIndex === index && activePart === EntryPart.Aggregator,
      label: (_b = (_a = stepSections[0].items[behavior.startStepNumber]) == null ? void 0 : _a.label) != null ? _b : `Select Step...`
    },
    props: {
      sections: stepSections
    },
    on: {
      change: handleStepSelection
    }
  })), isAggregationBehavior(behavior) && /* @__PURE__ */ jsx("div", {
    sel: ".subitem-text"
  }, /* @__PURE__ */ jsx(SelectEl, {
    sel: ".subitem-select",
    attrs: {
      elref: `aggregatorSelect`,
      "disable-search": true,
      tabindex: -1,
      theme: SelectTheme.QbSubitem,
      size: ButtonSize.Small,
      open: activeIndex === index && activePart === EntryPart.Aggregator,
      label
    },
    props: {
      sections
    },
    on: {
      close: () => section.helpers.setActiveEntry(`close`, { index, part: EntryPart.Aggregator }),
      open: () => section.helpers.setActiveEntry(`open`, { index, part: EntryPart.Aggregator }),
      change: handleAggregationSelection
    }
  })), /* @__PURE__ */ jsx(PropertySelector, {
    selected,
    handleSelect,
    index,
    isPlaceholder,
    section,
    selectorIcon,
    entry,
    displayCohorts,
    isForFilter,
    isInner: true
  }));
}
export function MultiAttributionLookbackSubItem({
  section,
  selected,
  index,
  handleSelect
}) {
  if (!isMultiAttributionSegmentation(selected)) {
    return null;
  }
  const { window } = selected.behavior.dateRange;
  const dateRangeLabel = `${window.value} ${capitalize(window.unit)} Lookback`;
  const { index: activeIndex, part: activePart } = section.state.openEntry;
  const isOpen = activeIndex === index && activePart === EntryPart.MultiAttributionLookback;
  return /* @__PURE__ */ jsx("div", {
    sel: ".subitem-text"
  }, /* @__PURE__ */ jsx(SelectEl, {
    sel: ".subitem-select",
    attrs: {
      elref: `multiAttributionLookbackSelect`,
      theme: SelectTheme.QbSubitem,
      size: ButtonSize.Small,
      label: dateRangeLabel,
      open: isOpen
    },
    on: {
      menuChange: (ev) => section.onMultiAttributionLookbackMenuChange(ev, index)
    }
  }, isOpen && /* @__PURE__ */ jsx(MultiAttributionLookbackEl, {
    attrs: {
      elref: `multiAttributionLookbackEl`,
      slot: `body`
    },
    params: {
      lookbackWindow: selected.behavior.dateRange.window,
      projectId: section.attr(`project-id`),
      handleCancel: () => section.onMultiAttributionLookbackCancel(),
      handleChange: (ev) => {
        const updatedSelection = produce(selected, (draft) => {
          draft.behavior.dateRange.window = { ...ev.detail.lookbackWindow };
        });
        handleSelect(updatedSelection);
      }
    }
  })));
}
export function MultiAttributionBehaviorFilterSubItem({
  section,
  selected,
  index,
  handleSelect
}) {
  const filter = section.getStagedMultiAttributionBehaviorFilter(index);
  if (!filter) {
    return null;
  }
  const { index: activeIndex, part: activePart } = section.state.openEntry;
  const isActiveEntry = activeIndex === index;
  const isFilterOperatorOpen = isActiveEntry && activePart === EntryPart.MultiAttributionBehaviorFilterOperator;
  const isFilterValueOpen = isActiveEntry && activePart === EntryPart.MultiAttributionBehaviorFilterValue;
  return /* @__PURE__ */ jsx("div", {
    sel: ".subitem-text.multi-attribution-behavior-filter"
  }, /* @__PURE__ */ jsx(SelectEl, {
    sel: ".subitem-select.multi-attribution-behavior-filter-operator",
    key: `subitem-${EntryPart.MultiAttributionBehaviorFilterOperator}`,
    attrs: {
      elref: `filterOperator`,
      open: isFilterOperatorOpen,
      theme: SelectTheme.QbSubitem,
      size: ButtonSize.Small,
      sections: section.getMultiAttributionBehaviorFilterOperatorSections({ index }),
      "menu-width": null
    },
    on: {
      menuChange: (ev) => section.onMultiAttributionBehaviorFilterOperatorMenuChange(ev, index),
      change: (ev) => {
        const selection = produce(selected, (s) => {
          s.behavior.filters[0].filterOperator = ev.detail.item.value;
        });
        handleSelect(selection);
      }
    }
  }), /* @__PURE__ */ jsx(SelectEl, {
    sel: ".subitem-select.multi-attribution-behavior-filter-value",
    key: `subitem-${EntryPart.MultiAttributionBehaviorFilterValue}`,
    attrs: {
      elref: `filterValueSelect`,
      label: filter.isValid() ? filter.getValuesLabel() : `Select value\u2026`,
      open: isFilterValueOpen,
      theme: SelectTheme.QbSubitem,
      size: ButtonSize.Small
    },
    on: {
      menuChange: (ev) => section.onMultiAttributionBehaviorFilterValueMenuChange(ev, index)
    }
  }, isFilterValueOpen && /* @__PURE__ */ jsx(PropertyValuesScreenEl, {
    attrs: {
      elref: `filterValue`,
      slot: `body`,
      "property-filter": filter.getAttributes(),
      "display-value-only": true,
      "hide-undefined-value": true,
      "project-id": section.attr(`project-id`),
      "workspace-id": section.attr(`workspace-id`)
    },
    on: {
      commitPropertyFilter: (ev) => {
        const selection = produce(selected, (s) => {
          s.behavior.filters = [ev.detail.propertyFilter.getAttributes()];
        });
        handleSelect(selection);
      }
    }
  })), !isFilterOperatorOpen && !isFilterValueOpen && /* @__PURE__ */ jsx(ButtonEl, {
    sel: ".delete-subitem-button",
    attrs: {
      theme: ButtonTheme.Tertiary,
      size: ButtonSize.Inline,
      icon: `x`,
      role: `button`,
      type: ButtonType.Destructive,
      tooltip: {
        text: `Remove touchpoint filter`,
        placement: Placement.Top
      }
    },
    on: {
      click: () => {
        const selection = produce(selected, (s) => {
          s.behavior.filters = [];
        });
        handleSelect(selection);
      }
    }
  }));
}
function addBehaviorToSegmentation(segmentation, behavior) {
  const defaultBehavior = isAggregationBehavior(behavior) ? DEFAULT_AGGREGATION_BEHAVIOR : DEFAULT_FREQUENCY_BEHAVIOR;
  return produce(segmentation, (draft) => {
    const behaviorDraft = {
      ...defaultBehavior,
      ...draft.behavior,
      ...behavior
    };
    draft.behavior = behaviorDraft;
    draft.propertyName = getColumnLabelForBehavior(behaviorDraft);
  });
}
function getPropertySelectionFromEventPropUpdate(ev, currentSelection, _isForFilter) {
  const { event, property } = ev.detail.item;
  return addBehaviorToSegmentation(currentSelection, { ...currentSelection.behavior, event, property });
}
function getPropertySelectionFromEventUpdate(ev, currentSelection, _isForFilter) {
  const item = ev.detail.item;
  const event = item;
  const overrides = {};
  if (event.value === SPECIAL_EVENTS.SELECTED_STEP) {
    overrides.startStepNumber = 0;
  }
  return addBehaviorToSegmentation(currentSelection, {
    ...currentSelection.behavior,
    event: { label: event.label, value: event.value },
    ...overrides
  });
}
function getPropertySelectionFromEvent(ev, currentSelection, isForFilter) {
  const { menuItemType, item, stagedItemsStack } = ev.detail;
  const isMultiAttributionBehavior = stagedItemsStack == null ? void 0 : stagedItemsStack.some(
    (item2) => item2.menuItemType === PropertiesMenu.MenuItemType.BehaviorMultiAttribution
  );
  let selection;
  if (!(stagedItemsStack == null ? void 0 : stagedItemsStack.length) && menuItemType === MenuItemType.BehaviorWithEvent) {
    const isAggregation = item[`value`] === BEHAVIOR_AGGREGATION.value || item[`propertyName`] === BEHAVIOR_AGGREGATION.value;
    selection = {
      ...DEFAULT_BEHAVIORLESS_SEGMENTATION,
      behavior: {
        ...isAggregation ? DEFAULT_AGGREGATION_BEHAVIOR : DEFAULT_FREQUENCY_BEHAVIOR,
        event: { custom: false, label: `Select Event`, value: null },
        property: getDefaultPropertyValueForBehavior(isAggregation)
      }
    };
  } else if (isMultiAttributionBehavior) {
    selection = {
      ...propertySelectionToSegmentation(item),
      behavior: {
        ...isMultiAttributionSegmentation(currentSelection) ? currentSelection.behavior : DEFAULT_MULTI_ATTRIBUTION_BEHAVIOR,
        property: item
      }
    };
  } else if (isForFilter) {
    selection = PropertyFilterFactory.createPropertyFilter(item, {
      displayContext: PropDisplayContext.Default
    });
  } else {
    selection = propertySelectionToSegmentation(item);
  }
  return selection;
}
function getInnerLabelRenderer(segment) {
  var _a;
  if (isMultiAttributionSegmentation(segment) || !isAggregationSegmentation(segment) || !(segment == null ? void 0 : segment.behavior.property)) {
    return null;
  }
  const behavior = segment.behavior;
  const event = renameEvent((_a = behavior == null ? void 0 : behavior.event) == null ? void 0 : _a.label);
  const propertyName = getPropertyName(behavior.property, {
    includeListItemGroup: true,
    showSpecialDatePropertyUnit: true
  });
  return () => /* @__PURE__ */ jsx(null, null, propertyName, /* @__PURE__ */ jsx("span", {
    sel: ".regular"
  }, " on "), event);
}
export function PropertySelector({
  entry,
  handleSelect,
  index,
  isPlaceholder,
  section,
  selectorIcon,
  displayCohorts = true,
  isForFilter = false,
  isInner = false,
  selected
}) {
  var _a, _b, _c, _d;
  const { index: activeIndex, part: activePart } = section.state.openEntry;
  const queryBlockConfig = section.getQueryBlockConfig(entry.type);
  const entryPart = isInner ? section.helpers.EntryPart.SubValue : section.helpers.EntryPart.Value;
  const behavior = Array.isArray(selected) ? null : selected == null ? void 0 : selected.behavior;
  const isEventProp = isInner && isAggregationBehavior(behavior);
  const isListOfObjectProp = isEventProp && section.params.listProperty;
  const isEvent = isInner && !isEventProp;
  const labelRenderer = isInner ? getInnerLabelRenderer(selected) : null;
  const listProperty = isEventProp && (behavior == null ? void 0 : behavior.property) ? propertySelectionToSegmentation(behavior.property) : section.params.listProperty;
  const eventSpecialAttrs = isEvent ? {
    type: `events`,
    selected: (_a = behavior == null ? void 0 : behavior.event) != null ? _a : { custom: false, label: `Select Event`, value: null }
  } : {};
  const eventPropSpecialAttrs = isEventProp && !isListOfObjectProp ? {
    type: `breakdown`,
    "show-special-date-property-unit": true,
    selected: {
      event: (_b = behavior == null ? void 0 : behavior.event) != null ? _b : { custom: false, label: `Select Event`, value: null },
      property: behavior == null ? void 0 : behavior.property
    }
  } : {};
  return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(QueryBlockEl, {
    key: "entry-selector",
    class: {
      "is-disabled": entry.readOnlyEvent,
      "is-placeholder": isPlaceholder && selected === null,
      "subitem-style": isInner
    },
    attrs: {
      selected: isForFilter ? { ...selected } : segmentationToPropertySelection(selected),
      elref: `entrySelector`,
      "apply-button-text": `Add ${section.attr(`section-title`)}`,
      "disable-value-selection": true,
      "min-selection": 1,
      "prefix-label": (_c = entry.prefixLabel) != null ? _c : void 0,
      "all-data-groups": section.attr(`data-groups-enabled`),
      "cohort-multiselect": true,
      "cohort-operation-type": isForFilter ? `filter` : `string`,
      "data-group-id": section.attr(`data-group-id`),
      "disabled-items": section.attr(`disabled-items`),
      "enable-enhanced-list-filters": section.attr(`enable-enhanced-list-filters`),
      "enable-workspace-filter-restrictions": section.attr(`enable-workspace-filter-restrictions`),
      "event-names": section.attr(`event-names`),
      "exclude-custom-events": section.attr(`exclude-custom-events`),
      "exclude-dimension-properties": section.attr(`exclude-dimension-properties`),
      "hide-edit-controls": section.attr(`hide-edit-controls`),
      "hide-events": section.attr(`hide-events`),
      "include-custom-properties": section.attr(`include-custom-properties`),
      "inline-cohorts-override": section.attr(`inline-cohorts-override`),
      "is-multi-attribution": isMultiAttributionSegmentation(selected),
      "project-has-cohorts": displayCohorts && section.attr(`project-has-cohorts`),
      "project-has-people": section.attr(`project-has-people`),
      "project-id": section.attr(`project-id`),
      "project-is-free-plan": section.attr(`project-is-free-plan`),
      "property-combine-mode": (_d = section.attr(`property-combine-mode`)) != null ? _d : void 0,
      readonly: section.attr(`readonly`),
      "selector-icon": selectorIcon != null ? selectorIcon : ``,
      "selector-type": `button`,
      "show-special-dataset-property": section.attr(`show-special-dataset-property`),
      "support-b2b-cohorts": section.attr(`support-b2b-cohorts`),
      "track-name": section.attr(`track-name`),
      "user-id": section.attr(`user-id`),
      "workspace-id": section.attr(`workspace-id`),
      autofocus: index === 0 && section.attr(`autofocus`) && isPlaceholder && !section.isStagedEntry(0),
      disabled: section.attr(`disabled`),
      open: isPlaceholder && activeIndex === index && activePart === entryPart,
      "show-all-tab": true,
      "button-size": isInner ? ButtonSize.Small : ButtonSize.Medium,
      theme: section.hasNewQBUI() ? ButtonTheme.QueryBuilder : entry.readOnlyEvent ? ButtonTheme.Tertiary : ButtonTheme.Select,
      ...queryBlockConfig,
      ...eventSpecialAttrs,
      ...eventPropSpecialAttrs
    },
    params: {
      listProperty,
      labelRenderer
    },
    props: { contextPanelParentElement: section.helpers.getContextPanelParentEl(index) },
    on: {
      close: () => section.helpers.setActiveEntry(`close`, { index, part: entryPart }),
      open: () => section.helpers.setActiveEntry(`open`, { index, part: entryPart }),
      remove: () => section.helpers.onEventActionClick(section.helpers.Action.Remove, index),
      track: (ev) => section.helpers.trackEntrySelectorEvent(ev, index),
      select: (ev) => {
        var _a2;
        const segment = selected;
        const handledEvent = isEvent ? getPropertySelectionFromEventUpdate(ev, segment, isForFilter) : isEventProp ? getPropertySelectionFromEventPropUpdate(ev, segment, isForFilter) : getPropertySelectionFromEvent(
          ev,
          segment,
          isForFilter
        );
        return handleSelect(handledEvent, null, (_a2 = ev[`detail`]) == null ? void 0 : _a2.trackingProps);
      }
    }
  }));
}
export function BehavioralSubFilters({ entry, handleSelect, index, section, selected }) {
  var _a, _b;
  if (!isEventBasedBehavioralSegmentation(selected)) {
    return null;
  }
  const behavior = selected.behavior;
  return /* @__PURE__ */ jsx(RowFilters, {
    entry,
    eventNames: ((_a = behavior.event) == null ? void 0 : _a.value) ? [behavior.event.value] : [],
    index,
    filters: (_b = behavior.filters) == null ? void 0 : _b.map((segmentation) => new PropertyFilter(segmentation)),
    selectedFiltersOperator: behavior.filtersOperator,
    extraClass: { "for-behaviors": true },
    onUpdate: (filters, filtersOperator, queryReason) => handleSelect(
      produce(selected, (item) => {
        item.behavior.filters = filters.map((filter) => filter.getAttributes());
        item.behavior.filtersOperator = filtersOperator;
      }),
      queryReason
    ),
    sectionAttrs: {
      "data-groups-enabled": false,
      "allow-cohort-filter": false,
      "can-rename-step": false,
      "project-has-people": false,
      "project-has-cohorts": false,
      "hide-events": false,
      "include-custom-properties": `all`
    },
    inlineCohortsOverride: section.attr(`inline-cohorts-override`),
    projectHasCohorts: section.attr(`project-has-cohorts`) && section.attr(`allow-cohort-filter`),
    hideEvents: !section.helpers.isEventBasedEntry(entry),
    isBehaviorMode: section.attr(`behavior-mode`),
    isReadOnly: section.attr(`readonly`),
    hasNewUI: section.hasNewQBUI(),
    nestedAttrs: section.helpers.getNestedSectionAttrs(),
    placeholders: section.state.placeholders.get(index),
    onAbortStage: () => section.helpers.setActiveEntry(`close`, { index, part: EntryPart.Filters })
  });
}
